import React from "react";
import PropTypes from "prop-types";
import EntityForm from "./EntityForm";
import { FluxActionBar as ActionBar } from "./ActionBar";
import FormStore from "data/meta/FormStore";
import userStore from "data/userStore";
import StereotypesStore from "data/StereotypesStore";
import { appAlert } from "lib/withOver";
import SidebarLayout from "../../sidebar/SidebarLayout";
import NoFormView from "./NoFormView";
import { FluxValidationBar as ValidationBar } from "./ValidationBar";
import { getActiveRecord } from "data/selectors/records";
import TableStore from "data/TableStore";
import TableAccess from "data/util/TableAccess";
import NavigatorStore from "data/NavigatorStore";
import EntityName from "data/util/EntityName";
import { getTopLevelRecords } from "data/selectors/superRecords";
import SuperRecordStore from "data/SuperRecordStore";
import {
    buildExcludesFilter,
    buildRecordItem,
} from "lib/dataentry/lib/validationtree/containers/ValidationTreeContainer";
import LiveMap from "data/util/LiveMap";
import { getImplementations } from "data/selectors/stereotypes";
import userEditedRecordStore from "data/userEditedRecordStore";
import uniqueFilterBuilder from "data/util/uniqueFilterBuilder";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import isMissing from "data/util/isMissing";
import Dispatcher from "Dispatcher";
import NavigationActions from "data/NavigationActions";
import ClientId from "data/ClientId";
import SuperRecordActions from "data/SuperRecordActions";
import useFluxStore from "@armus/use-flux-store";

import { asMarionetteView } from "lib/common/hoc/BackboneReact/asMarionetteView";

function sharedPrefix(a, b) {
    const l = Math.min(a.length, b.length);
    let prefix = "";
    let c;
    for (let i = 0; i < l; i++) {
        c = a.charAt(i);
        if (b.charAt(i) !== c) break;
        prefix += c;
    }
    return prefix;
}

const getMissingRequiredFields = (fluxContext, activeKey="") =>
    // for each edited entity record in this context
    userEditedRecordStore.getRecordKeys(fluxContext)
        .filter(k => TableStore.hasEntityRecord(k.key, k.id))
        .map(k => TableStore.getEntityProxy(k.key, k.id))
    //     find its top-level record
        .map(ep => {
            do {
                const parent = ep.getParent();
                if (parent == null) {
                    return ep;
                }
                ep = parent;
            } while (true);
        })
        .filter(uniqueFilterBuilder(ep => ep.getKey()))
    //     find its table record
        .map(ep => ep.tableProxy)
    //     find every fronting entity record
        .flatMap(tp => tp.getEntityProxies())
    //     find all records in its entire composition tree
        .flatMap(ep => {
            const queue = [ep];
            const result = [];
            while (queue.length) {
                const curr = queue.pop();
                result.push(curr);
                queue.push(...curr.entityInfo.associations.cmv
                    .flatMap(c => curr.getField(c)));
            }
            return result;
        })
    //     find all fields which are required, enabled, and missing
        .flatMap(ep => entityFieldMetaStore
            .getAllInfos(ep.entityKey, fi =>
                fi.requiredInDataEntry)
            .filter(fi => ep.isFieldEnabled(fi))
            .filter(fi => isMissing(ep.getField(fi)))
            .map(fi => [ep, fi])
        )
    // pick the one "closest" to the active record
        .sort(([a], [b]) =>
            // longest matching prefix first
            sharedPrefix(b.getKey(), activeKey).length - sharedPrefix(a.getKey(), activeKey).length)
    // deduplicate by table column
        .filter(uniqueFilterBuilder(([ep, fi]) =>
            ep.tableProxy.tableKey + "." + fi.logicalKey))
    ;

const disallowSaveBecauseIllegal = fluxContext => {
    const magic = NavigatorStore.getMagicState(fluxContext);
    const srInfo = SuperRecordStore.getRecord(magic.key, magic.id);
    const stereoInfo = StereotypesStore.getStereotypeByKey(magic.key);
    const access = new TableAccess(TableStore.getState());
    const stereoImplementations = getImplementations(
        stereoInfo,
        access,
        magic.id,
    );
    const canSaveIllegals = new LiveMap(implKey =>
        userStore.getLicensePreference(
            "allow-saving-illegal-values",
            implKey,
        ));
    const excludeFilters = new LiveMap(implKey =>
        buildExcludesFilter(implKey));
    const reduceWork = (hi, ep) => {
        if (hi) return hi;
        const implKey = new EntityName(ep.entityKey).implKey;
        const {meta} = buildRecordItem(
            ep,
            excludeFilters.get(implKey),
            () => true, // do everything
        );
        if (meta._is_blocking || meta._contains_blocking) {
            return true;
        }
        if (canSaveIllegals.get(implKey)) {
            return false;
        }
        return meta._is_illegal || meta._contains_illegal;
    };
    return getTopLevelRecords(TableStore, srInfo)
        .reduce(reduceWork, stereoImplementations
            .map(impl =>
                TableStore.getEntityProxy(impl.key, magic.id))
            .reduce(reduceWork, false));
};

const MarionetteActionBar = asMarionetteView(ActionBar);
const MarionetteValidationBar = asMarionetteView(ValidationBar);
const MarionetteSidebar = asMarionetteView(SidebarLayout, {className: "sidebar"});
const MarionetteEntityForm = asMarionetteView(EntityForm);
const MarionetteNoFormView = asMarionetteView(NoFormView);

class BottomPane extends Backbone.Marionette.Layout {
    constructor(...args) {
        super(...args);
        this.initialize = this.initialize.bind(this);
        this.onRender = this.onRender.bind(this);
        this.showNoForm = this.showNoForm.bind(this);
        this.showForm = this.showForm.bind(this);
    }

    initialize(options){
        this.fluxContext = options.fluxContext;
        this.isModal = options.isModal;
        this.showTreeNavigator = options.showTreeNavigator;
    }

    onRender() {
        this.showNoForm();
    }

    showNoForm() {
        this.form.show(new MarionetteNoFormView());
    }

    showForm() {
        const nothingToSeeHere = () => {
            this.ef && this.ef.close();
            this.ab && this.ab.close();
            this.vb && this.vb.close();
            this.showNoForm();
        };
        const fluxContext = this.fluxContext;
        const activeRecord = getActiveRecord(TableStore, NavigatorStore.getRecordState(fluxContext));

        if (this.sb == null) {
            this.sb = new MarionetteSidebar({
                fluxContext
            });
            this.sidebar.show(this.sb);
        }

        if (activeRecord == null) {
            return nothingToSeeHere();
        }

        const lo = FormStore.getFormLO(activeRecord.entityKey);
        if (!lo.hasValue()) {
            return nothingToSeeHere();
        }
        const descriptor = lo.getRequiredValue();
        if ((descriptor.childItems == null) || (descriptor.childItems.length === 0)) {
            return nothingToSeeHere();
        }

        this.ef = new MarionetteEntityForm({
            descriptor,
            entityKey: activeRecord.entityKey,
            id: activeRecord.id,
            fluxContext
        });


        this.ab = new MarionetteActionBar({
            fluxContext,
            recordId: activeRecord.id,
            entityKey: activeRecord.entityKey,
            versionDropdownEnabled: this.showTreeNavigator,

            isModal: this.isModal,
            allowSave: ClientId.is(activeRecord.id)
                ? activeRecord.canCreate()
                : activeRecord.canWrite(),
            allowCancel: true,
            allowSectionJump: true,
            onSave: () => {
                if (disallowSaveBecauseIllegal(fluxContext)) {
                    appAlert(`Cannot save with current errors. Please review record and correct noted errors.`);
                    return;
                }
                const missingRequireds = getMissingRequiredFields(fluxContext, activeRecord.getKey());
                if (missingRequireds.length > 0) {
                    let maxToShow = 5;
                    if (missingRequireds.length === maxToShow + 1) {
                        maxToShow = missingRequireds.length;
                    }
                    const jumpTarget = missingRequireds[0];
                    const jumpProxy = jumpTarget[0];
                    const jumpFieldInfo = jumpTarget[1];
                    appAlert({
                        title: "Missing Required Fields",
                        message() {
                            return <React.Fragment>
                                <p>You must enter a value for the following {missingRequireds.length === 1 ? "field" : "fields"}:
                                </p>

                                <ul>
                                    {missingRequireds.slice(0, maxToShow)
                                        .map(([ep, fi], i) =>
                                            <li key={i}>
                                                {ep.getLabel()} &gt; {fi.name}
                                            </li>
                                        )}
                                    {missingRequireds.length > maxToShow
                                    && <li>And {missingRequireds.length - maxToShow} more...</li>}
                                </ul>
                            </React.Fragment>;
                        },
                        actions: [
                            {
                                label: `Go to ${jumpFieldInfo.name}`,
                                callback: () => Dispatcher.contextDispatch(fluxContext, {
                                    type: NavigationActions.SELECT_RECORD,
                                    entityKey: jumpProxy.entityKey,
                                    recordId: jumpProxy.id,
                                    fieldKey: jumpFieldInfo.key,
                                }),
                            },
                            {
                                label: "Cancel",
                            },
                        ]
                    });
                    return;
                }
                Dispatcher.contextDispatch(fluxContext, {
                    type: SuperRecordActions.SAVE
                });
            }
        });

        this.vb = new MarionetteValidationBar({
            fluxContext,
        });

        this.actionbar.show(this.ab);
        this.form.show(this.ef);
        this.validationbar.show(this.vb);

        // listen for events from adapters that we need to translate to localized events for this de module
        this.ef.listenTo(this.ef, "ef:record:new-child", (record, fieldKey, entityKey, options) => {
            throw new Error("Don't do that; dispatch 'record:new-child' directly");});
        this.ef.listenTo(this.ef, "widget:click", config => {
            throw new Error("Don't do that; flux dispatch 'WidgetActions.WIDGET_CLICK' directly");});
    }

}

BottomPane.prototype.template = require("./templates/bottom_pane.hbs");
BottomPane.prototype.regions = {
    actionbar: "header.header",
    form: "section.entity-form-container",
    validationbar: ".form.footer",
    sidebar: ".de_sidebar"
};

export default BottomPane;



// TODO: BottomPane react version. This was started but is not quite ready to be used.

const BottomPaneView = (props) => {
    const { fluxContext, showTreeNavigator, isModal } = props;
    const activeRecord = getActiveRecord(TableStore, NavigatorStore.getRecordState(fluxContext));
    const entityKey = activeRecord ? activeRecord.entityKey : null;
    const id = activeRecord ? activeRecord.id : null;

    const { descriptor } = useFluxStore(() => {
            let descriptor = null;
            if(activeRecord !== null){
                const lo = FormStore.getFormLO(entityKey);
                if(lo.hasValue()) {
                    descriptor = lo.getRequiredValue();
                }
            }
            return {
                descriptor,
            };
        }
        , [
            FormStore,
            NavigatorStore,
        ], [fluxContext, entityKey, id]);

    const onSave = React.useCallback(() => {
        if (disallowSaveBecauseIllegal(fluxContext)) {
            appAlert(`Cannot save with current errors. Please review record and correct noted errors.`);
            return;
        }
        const missingRequireds = getMissingRequiredFields(fluxContext, activeRecord.getKey());
        if (missingRequireds.length > 0) {
            let maxToShow = 5;
            if (missingRequireds.length === maxToShow + 1) {
                maxToShow = missingRequireds.length;
            }
            const jumpTarget = missingRequireds[0];
            const jumpProxy = jumpTarget[0];
            const jumpFieldInfo = jumpTarget[1];
            appAlert({
                title: "Missing Required Fields",
                message() {
                    return <React.Fragment>
                        <p>You must enter a value for the following {missingRequireds.length === 1 ? "field" : "fields"}:
                        </p>

                        <ul>
                            {missingRequireds.slice(0, maxToShow)
                                .map(([ep, fi], i) =>
                                    <li key={i}>
                                        {ep.getLabel()} &gt; {fi.name}
                                    </li>
                                )}
                            {missingRequireds.length > maxToShow
                            && <li>And {missingRequireds.length - maxToShow} more...</li>}
                        </ul>
                    </React.Fragment>;
                },
                actions: [
                    {
                        label: `Go to ${jumpFieldInfo.name}`,
                        callback: () => Dispatcher.contextDispatch(fluxContext, {
                            type: NavigationActions.SELECT_RECORD,
                            entityKey: jumpProxy.entityKey,
                            recordId: jumpProxy.id,
                            fieldKey: jumpFieldInfo.key,
                        }),
                    },
                    {
                        label: "Cancel",
                    },
                ]
            });
            return;
        }

        Dispatcher.contextDispatch(fluxContext, {
            type: SuperRecordActions.SAVE
        });
    }, [fluxContext]);

    if(activeRecord == null) {
        return <NoFormView />;
    }
    let body = null;
    if(descriptor !== null) {
        if(!((descriptor.childItems == null) || (descriptor.childItems.length === 0))) {
            body = (
                <React.Fragment>
                    <header className="form header">
                        <ActionBar
                            fluxContext={fluxContext}
                            entityKey={activeRecord.entityKey}
                            recordId={activeRecord.id}
                            versionDropdownEnabled={showTreeNavigator}

                            isModal={isModal}
                            allowSave={ClientId.is(activeRecord.id)
                                ? activeRecord.canCreate()
                                : activeRecord.canWrite()}
                            allowCancel={true}
                            allowSectionJump={true}
                            onSave={onSave}
                        />
                    </header>
                    <section className="entity-form-container">
                        <EntityForm
                            fluxContext={fluxContext}
                            entityKey={activeRecord.entityKey}
                            id={activeRecord.id}
                            descriptor={descriptor}
                        />
                    </section>

                    <header className="form footer">
                        <ValidationBar
                            fluxContext={fluxContext}
                        />
                    </header>
                </React.Fragment>
            );
        }
    }

    return (
        <React.Fragment>
            <div className="de_pane">
                {body}
            </div>
            <div className="de_sidebar">
                <SidebarLayout
                    fluxContext={fluxContext}
                />
            </div>
        </React.Fragment>
    );
};

BottomPaneView.propTypes = {
    fluxContext: PropTypes.string.isRequired,
    showTreeNavigator: PropTypes.bool,
    isModal: PropTypes.bool,
};

//export default BottomPaneView;




// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/dataentryform/views/BottomPane.js